import * as React from 'react'
import usePublicEventDelete from '../../../hooks/usePublicEventDelete'
import VerificationCodeDialog from '../../VerificationCodeDialog'
import {
  setPendingDelete,
  getPendingDelete,
  clearPendingDelete,
} from '../../../utils/pendingDelete'

const PENDING_TABLE = 'publicEvents'

/**
 * Flujo de eliminación de evento de prueba con OTP a nivel de tabla. Controlado
 * de forma imperativa (`ref.openModal(event)`) para sobrevivir a refetch/re-render
 * del DataGrid mientras el usuario lee el código en su correo. Reutiliza el hook
 * `usePublicEventDelete`. Persiste el borrado en curso para reabrirse tras una
 * recarga de página.
 */
const DeletePublicEventModal = React.forwardRef((_props, ref) => {
  const [event, setEvent] = React.useState(null)
  const [pendingRequest, setPendingRequest] = React.useState(false)
  const prevOtpOpenRef = React.useRef(false)

  const {
    otpDialogOpen,
    otpLoading,
    otpError,
    resendCount,
    retryAfterSeconds,
    requestDeletion,
    requestNewCode,
    confirmDeletion,
    closeOtpDialog,
  } = usePublicEventDelete(event?.id)

  React.useImperativeHandle(ref, () => ({
    openModal: targetEvent => {
      if (!targetEvent?.id) return
      setEvent(targetEvent)
      setPendingRequest(true)
    },
  }))

  // La solicitud se dispara tras setear el evento, para que el hook ya tenga el
  // id correcto en su closure.
  React.useEffect(() => {
    if (pendingRequest && event) {
      setPendingRequest(false)
      requestDeletion()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRequest, event])

  // El OTP vive en el hook: sincronizar el slot de persistencia observando la
  // apertura/cierre del diálogo.
  React.useEffect(() => {
    if (otpDialogOpen && event) {
      setPendingDelete({ table: PENDING_TABLE, id: event.id, label: event.code })
    } else if (!otpDialogOpen && prevOtpOpenRef.current) {
      clearPendingDelete()
    }
    prevOtpOpenRef.current = otpDialogOpen
  }, [otpDialogOpen, event])

  // Restaurar tras una recarga: reabrir el OTP del mismo evento (en cooldown
  // reabre el diálogo; si el cooldown venció, reenvía un código nuevo).
  React.useEffect(() => {
    const pending = getPendingDelete(PENDING_TABLE)
    if (pending) {
      setEvent({ id: pending.id, code: pending.label })
      setPendingRequest(true)
    }
  }, [])

  return (
    <VerificationCodeDialog
      open={otpDialogOpen}
      onClose={closeOtpDialog}
      onConfirm={confirmDeletion}
      onRequestNew={requestNewCode}
      loading={otpLoading}
      resourceLabel={event ? `Evento ${event.code}` : ''}
      error={otpError}
      resendCount={resendCount}
      retryAfterSeconds={retryAfterSeconds}
    />
  )
})

DeletePublicEventModal.displayName = 'DeletePublicEventModal'

export default DeletePublicEventModal
